import { ApolloError } from 'apollo-client';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  BenefitType,
  IBenefit,
  IBenefitSwap,
  ILoyaltyUserQuery,
  IRewardFragment,
  IRuleConditionEvaluation,
  OfferRedemptionType,
  PaymentMethod,
  useLoyaltyUserQuery,
} from 'generated/graphql-gateway';
import {
  INotificationDetailsFragment,
  IRewardOrSystemwideOfferOrSystemwidePromotion,
  useFeatureSortedLoyaltyOffersQuery,
} from 'generated/sanity-graphql';

import { IEngineRewardsMap, LoyaltyAppliedOffer } from '../types';

export type LoyaltyUser = ILoyaltyUserQuery['loyaltyUser'];

export type AvailableRewards = {
  [id: string]: IRewardFragment;
};

export interface IUseLoyalty {
  loyaltyEnabled: boolean;
  loyaltyLoading: boolean;
  loyaltyUser: LoyaltyUser | null;
  refetchLoyaltyUser: ReturnType<typeof useLoyaltyUserQuery>['refetch'];
  claimPointsEnabled: boolean;
}

export type UseLoyaltyRewards = (loyaltyUser: LoyaltyUser | null) => IUseLoyaltyRewards;

interface IAppliedReward {
  timesApplied: number;
  rewardId: string;
  pointCost: number;
  rewardBenefitId: string;
  sanityId?: string;
}

export interface IAppliedRewards {
  [cartId: string]: IAppliedReward;
}
export interface IParsedLoyaltyReward {
  cartId: string;
  rewardId: string;
  timesApplied: number;
  sanityId?: string;
}

export interface IParseAppliedRewards {
  appliedRewardsArray: IParsedLoyaltyReward[];
  timesRewardApplied: { [rewardId: string]: number };
}
export interface IUseLoyaltyOffers {
  queryLoyaltyUserStandardOffers: (options: IQueryLoyaltyUserOffersOptions) => void;
  queryLoyaltyUserSurpriseOffers: (options: IQueryLoyaltyUserOffersOptions) => void;
  loyaltyOffersEnabled: boolean;
  loyaltyStandardOffersEnabled: boolean;
  loyaltySurpriseOffersEnabled: boolean;
  queryLoyaltyOffers: (options: { subtotalAmount?: number }) => void;
  evaluateLoyaltyUserIncentives: (
    loyaltyId: string,
    appliedLoyaltyOffers: LoyaltyAppliedOffer[],
    cartEntries?: ICartEntry[],
    appliedRewards?: IAppliedRewards,
    subtotalAmount?: number,
    paymentMethod?: PaymentMethod | null
  ) => void;
  refetchCmsOffers: ReturnType<typeof useFeatureSortedLoyaltyOffersQuery>['refetch'];
}
export interface IUseLoyaltyRewards {
  getAvailableRewardFromCartEntry: (cartEntry: ICartEntry) => IRewardFragment | undefined;
  refetchRewards: (loyaltyId: string) => void;
  engineRewardsMap: IEngineRewardsMap;
  isLimitRewardRedemptionPerOrderReached: boolean;
  rewardLimitePerOrder: number | null | undefined;
}
export interface IUseLoyaltyRewardsFeedback {
  loading: boolean;
  error?: ApolloError;
}

export interface IRewardAction {
  cartId: string;
  rewardBenefitId: string;
  quantity?: number;
}

export interface IRemoveAppliedRewardsAction {
  cartIds: string[];
}

// Swap type guard
export const isSwap = (benefit: IBenefit): benefit is IBenefitSwap => {
  return (benefit as IBenefitSwap)?.value && benefit.type === BenefitType.SWAP;
};

export type NotificationDetails = INotificationDetailsFragment & {
  id: IRewardOrSystemwideOfferOrSystemwidePromotion['loyaltyEngineId'];
};

export interface IUseLoyaltyNotifications {
  notifications: NotificationDetails[];
  loadingNotifications: boolean;
}
export interface IQueryLoyaltyUserOffersOptions {
  loyaltyId: string;
  cartEntries?: ICartEntry[];
  appliedRewards?: IAppliedRewards;
  subtotalAmount?: number;
  redemptionTypes?: OfferRedemptionType[];
  appliedLoyaltyOffers?: LoyaltyAppliedOffer[];
}

export type LoyaltyNotifications = (
  loyaltyUser: LoyaltyUser | null,
  loyaltyOffersEnabled: boolean
) => IUseLoyaltyNotifications;

export type RedeemableItem = {
  readonly id: string;
};

export type RedeemableItems = readonly (RedeemableItem | null)[] | null;

export type IIncentiveEvaluationResult = Omit<IRuleConditionEvaluation, '__typename'>;

export type IncentiveEvaluationMap = { [incentiveId: string]: IIncentiveEvaluationResult[] };

export enum IncentiveEvaluationErrorCodes {
  UNSATISFIED_CART_REQUIREMENTS = 'unsatisfied-cart-requirements',
  UNSATISFIED_CART_REQUIREMENTS_EXCLUSION = 'unsatisfied-cart-requirements-exclusion',
  WITHIN_COOL_DOWN_PERIOD = 'within-cool-down-period',
  EXCEEDS_LIMIT_PER_ORDER = 'exceeds-limit-per-order',
  INVALID_SERVICE_MODE = 'invalid-service-mode',
  INVALID_STORE_ID = 'invalid-store-id',
  NOT_FIRST_ORDER = 'not-first-order',
  NOT_USER_BIRTHDAY = 'not-user-birthday',
  INVALID_END_DATE = 'invalid-end-date',
  INVALID_START_DATE = 'invalid-start-date',
  INVALID_DAY_OF_THE_WEEK = 'invalid-day-of-the-week',
  INVALID_STACKING = 'invalid-stacking',
  EXCEEDS_MAX_REDEMPTION = 'exceeds-max-redemption',
  BELOW_MINIMUM_SPEND = 'below-minimum-spend',
  INVALID_PAYMENT_METHOD = 'invalid-payment-method',
  INSUFFICIENT_POINT_BALANCE = 'insufficient-point-balance',
  INVALID_SWAPS = 'invalid-swaps',
  INVALID_TIME_SPAN = 'invalid-time-span',
  USER_ENROLLMENT = 'user-enrollment',
  OUT_OF_DAY_PART = 'out-of-day-part',
  NOT_AVAILABLE_IN_STORE = 'not-available-in-store',
  OFFER_NOT_AVAILABLE = 'offer-not-available',
}

export const FixableEvaluationErrors = new Set([
  IncentiveEvaluationErrorCodes.BELOW_MINIMUM_SPEND,
  IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS,
  IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD,
  IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE,
]);

export interface IUseLoyaltyUserState {
  loyaltyUser: LoyaltyUser;
  error: ApolloError | undefined;
  refetch: ReturnType<typeof useLoyaltyUserQuery>['refetch'];
  loading: boolean;
}
